import { resolveContainerKey, type TContainerKey } from '@/schoolClasses/containerTranslationKeys';
import { useGroup, type TViewedSchoolClass } from '@/schoolClasses/useGroup';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';

interface IContainerTranslations {
  isGroup: ComputedRef<boolean>;
  tContainer: (key: TContainerKey, named?: Record<string, unknown>) => string;
}

/**
 * Translates the wordings that differ between classes and groups, picking the variant from
 * `useGroup` so components no longer branch on it themselves. Named values are passed through to
 * `t()` unchanged, which also covers pluralization (vue-i18n reads `count` from them).
 */
export const useContainerTranslations = (
  schoolClass?: MaybeRefOrGetter<TViewedSchoolClass | null | undefined>,
): IContainerTranslations => {
  const { isGroup } = useGroup(schoolClass);
  const { t } = useI18n();

  const tContainer = (key: TContainerKey, named?: Record<string, unknown>): string => {
    const resolvedKey = resolveContainerKey(key, isGroup.value);
    return named ? t(resolvedKey, named) : t(resolvedKey);
  };

  return { isGroup, tContainer };
};
